import React from 'react';
import { Patient } from '../../../services/firebase/FirebasePatientService';
import { db } from '../../../config/firebase';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import AudioPlayer from '../../AudioPlayer';
import { audioStorageService } from '../../../services/AudioStorageService';
import ClinicalNotesEditor from '../../ClinicalNotesEditor';
import { useAuth } from '../../../contexts/AuthContext';

interface SessionsTabImplementationProps {
  client: Patient;
  onStartSession?: (patient: Patient) => void;
}

const SessionsTabImplementation: React.FC<SessionsTabImplementationProps> = ({ client, onStartSession }) => {
  const { user } = useAuth();
  const [sessions, setSessions] = React.useState<any[]>([]);
  const [expandedSession, setExpandedSession] = React.useState<string | null>(null);
  const [searchDate, setSearchDate] = React.useState('');
  const [selectedSessionType, setSelectedSessionType] = React.useState<string>('all');
  const [audioUrls, setAudioUrls] = React.useState<Record<string, { original?: string; filtered?: string }>>({});
  const [showNotesEditor, setShowNotesEditor] = React.useState(false);
  const [selectedSessionForNotes, setSelectedSessionForNotes] = React.useState<any | null>(null);

  React.useEffect(() => {
    loadSessions();
    initializeAudioStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const initializeAudioStorage = async () => {
    try {
      await audioStorageService.initialize();
      console.log('Audio storage initialized');
    } catch (error) {
      console.error('Failed to initialize audio storage:', error);
    }
  };

  const loadSessions = async () => {
    if (!user?.uid) return;
    try {
      const q = query(
        collection(db, 'session_recordings'),
        where('patientId', '==', client.id),
        where('providerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const firestoreSessions = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          sessionId: data.sessionId || doc.id,
          sessionDate: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
          sessionType: data.sessionType || 'therapy',
          duration: data.duration || 0,
          providerId: data.providerId,
          status: data.status || 'completed',
          clinicalNotes: data.noteSuggestion?.content || data.clinicalNotes || '',
          transcription: data.transcriptionText || '',
          ...data
        };
      });
      setSessions(firestoreSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadAudioForSession = async (sessionId: string) => {
    try {
      const originalUrl = await audioStorageService.getAudioUrl(sessionId, 'original');
      const filteredUrl = await audioStorageService.getAudioUrl(sessionId, 'filtered');

      if (originalUrl || filteredUrl) {
        setAudioUrls(prev => ({
          ...prev,
          [sessionId]: {
            original: originalUrl || undefined,
            filtered: filteredUrl || undefined
          }
        }));
      }
    } catch (error) {
      console.error('Failed to load audio for session:', error);
    }
  };

  const handleDownloadAudio = async (sessionId: string, type: 'original' | 'filtered') => {
    try {
      await audioStorageService.downloadAudio(sessionId, type);
    } catch (error) {
      console.error('Failed to download audio:', error);
    }
  };

  // Load audio when session is expanded
  React.useEffect(() => {
    if (expandedSession) {
      loadAudioForSession(expandedSession);
    }
  }, [expandedSession]);

  const handleOpenNotesEditor = (session: any) => {
    setSelectedSessionForNotes(session);
    setShowNotesEditor(true);
  };

  const handleCloseNotesEditor = () => {
    setShowNotesEditor(false);
    setSelectedSessionForNotes(null);
  };

  const handleNoteSaved = () => {
    // Refresh sessions to show updated notes
    loadSessions();
  };

  const filteredSessions = sessions.filter(session => {
    if (searchDate && !session.sessionDate.startsWith(searchDate)) {
      return false;
    }
    if (selectedSessionType !== 'all' && session.sessionType !== selectedSessionType) {
      return false;
    }
    return true;
  });

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSessionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'initial-consultation': 'Initial Consultation',
      'follow-up': 'Follow-Up',
      'therapy': 'Therapy Session',
      'assessment': 'Assessment',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'completed': '#10b981',
      'cancelled': '#ef4444',
      'no-show': '#f59e0b'
    };
    return colors[status] || '#6b7280';
  };

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#F7E9A0',
            marginBottom: '8px'
          }}>
            Sessions & Transcripts
          </h3>
          <p style={{ color: '#e5e7eb', margin: 0 }}>
            Session history, notes, and transcripts for {`${client.firstName} ${client.lastName}`}.
          </p>
        </div>
        {onStartSession && (
          <button
            onClick={() => onStartSession(client)}
            style={{
              backgroundColor: '#6CA6C1',
              color: '#1e293b',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#7fb5cc';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6CA6C1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            + Start New Session
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#F7E9A0',
            marginBottom: '6px'
          }}>
            Search by Date
          </label>
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#16325B',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#e5e7eb',
              fontSize: '0.875rem'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#F7E9A0',
            marginBottom: '6px'
          }}>
            Session Type
          </label>
          <select
            value={selectedSessionType}
            onChange={(e) => setSelectedSessionType(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#16325B',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#e5e7eb',
              fontSize: '0.875rem'
            }}
          >
            <option value="all">All Types</option>
            <option value="initial-consultation">Initial Consultation</option>
            <option value="follow-up">Follow-Up</option>
            <option value="therapy">Therapy Session</option>
            <option value="assessment">Assessment</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div style={{
          backgroundColor: '#16325B',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1'
        }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic' }}>
            {sessions.length === 0
              ? 'No sessions recorded yet. Sessions and transcripts will appear here after appointments.'
              : 'No sessions match your search criteria.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              style={{
                backgroundColor: '#16325B',
                borderRadius: '6px',
                border: '1px solid #6CA6C1',
                overflow: 'hidden'
              }}
            >
              {/* Session Header */}
              <div
                onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                style={{
                  padding: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s',
                  backgroundColor: expandedSession === session.id ? '#1e4976' : 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e4976'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = expandedSession === session.id ? '#1e4976' : 'transparent'}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#F7E9A0'
                    }}>
                      {getSessionTypeLabel(session.sessionType)}
                    </h4>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: getStatusColor(session.status) + '20',
                        color: getStatusColor(session.status),
                        border: `1px solid ${getStatusColor(session.status)}`
                      }}
                    >
                      {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#d1d5db' }}>
                    {formatDateTime(session.sessionDate)} • Duration: {formatDuration(session.duration)}
                  </div>
                  {session.appointmentReason && (
                    <div style={{ fontSize: '0.875rem', color: '#e5e7eb', marginTop: '4px' }}>
                      Reason: {session.appointmentReason}
                    </div>
                  )}
                </div>
                <div style={{
                  fontSize: '1.25rem',
                  color: '#6CA6C1',
                  transform: expandedSession === session.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}>
                  ▼
                </div>
              </div>

              {/* Expanded Session Details */}
              {expandedSession === session.id && (
                <div style={{
                  padding: '16px',
                  borderTop: '1px solid #6CA6C1',
                  backgroundColor: '#0d1b2a'
                }}>
                  {/* Filtering Stats */}
                  {session.filteringStats && (
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#F7E9A0',
                        marginBottom: '8px'
                      }}>
                        Content Filtering Statistics
                      </h5>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#16325B',
                        borderRadius: '6px'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>Total Words</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e5e7eb' }}>
                            {session.filteringStats.totalWords}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>Filtered Words</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444' }}>
                            {session.filteringStats.filteredWords}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>Filtering Rate</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f59e0b' }}>
                            {session.filteringStats.filteringPercentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>

                      {/* Category Breakdown */}
                      {Object.keys(session.filteringStats.categoryBreakdown || {}).length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <h6 style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#d1d5db',
                            marginBottom: '6px'
                          }}>
                            Filtered by Category
                          </h6>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {Object.entries(session.filteringStats.categoryBreakdown).map(([category, count]: [string, any]) =>
                              <span
                                key={category}
                                style={{
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  backgroundColor: '#1e4976',
                                  color: '#e5e7eb'
                                }}
                              >
                                {category}: {count}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Audio Playback */}
                  {audioUrls[session.id] && (audioUrls[session.id].filtered || audioUrls[session.id].original) && (
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#F7E9A0',
                        marginBottom: '8px'
                      }}>
                        Audio Recordings
                      </h5>

                      {/* Filtered Audio */}
                      {audioUrls[session.id].filtered && (
                        <div style={{ marginBottom: '12px' }}>
                          <h6 style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#10b981',
                            marginBottom: '6px'
                          }}>
                            Filtered Audio (Patient-Safe)
                          </h6>
                          <AudioPlayer
                            audioUrl={audioUrls[session.id].filtered!}
                            title="Filtered Audio"
                            showWaveform={true}
                            showDownload={true}
                            onDownload={() => handleDownloadAudio(session.id, 'filtered')}
                          />
                        </div>
                      )}

                      {/* Original Audio */}
                      {audioUrls[session.id].original && (
                        <div>
                          <h6 style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#f59e0b',
                            marginBottom: '6px'
                          }}>
                            Original Audio (Provider-Only)
                          </h6>
                          <AudioPlayer
                            audioUrl={audioUrls[session.id].original!}
                            title="Original Audio"
                            showWaveform={true}
                            showDownload={true}
                            onDownload={() => handleDownloadAudio(session.id, 'original')}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transcripts */}
                  <div style={{ marginBottom: '20px' }}>
                    <h5 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#F7E9A0',
                      marginBottom: '8px'
                    }}>
                      Transcripts
                    </h5>

                    {/* Filtered Transcript */}
                    {session.filteredTranscript && (
                      <div style={{ marginBottom: '12px' }}>
                        <h6 style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#10b981',
                          marginBottom: '6px'
                        }}>
                          Filtered Transcript (Patient-Safe)
                        </h6>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#16325B',
                          borderRadius: '6px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          fontSize: '0.875rem',
                          color: '#e5e7eb',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6'
                        }}>
                          {session.filteredTranscript}
                        </div>
                      </div>
                    )}

                    {/* Original Transcript */}
                    {session.originalTranscript && (
                      <div>
                        <h6 style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#f59e0b',
                          marginBottom: '6px'
                        }}>
                          Original Transcript (Provider-Only)
                        </h6>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#16325B',
                          borderRadius: '6px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          fontSize: '0.875rem',
                          color: '#e5e7eb',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6'
                        }}>
                          {session.originalTranscript}
                        </div>
                      </div>
                    )}

                    {!session.filteredTranscript && !session.originalTranscript && (
                      <p style={{ fontSize: '0.875rem', color: '#d1d5db', fontStyle: 'italic' }}>
                        No transcripts available for this session.
                      </p>
                    )}
                  </div>

                  {/* AI Clinical Notes Button */}
                  <div style={{ marginBottom: '20px' }}>
                    <button
                      onClick={() => handleOpenNotesEditor(session)}
                      style={{
                        background: '#D4A017',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        color: '#1f2937',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>✨</span>
                      {session.clinicalNotes ? 'View/Edit Clinical Notes' : 'Generate AI Clinical Notes'}
                    </button>
                  </div>

                  {/* Clinical Notes — formatted by section headers */}
                  {session.clinicalNotes && (
                    <div>
                      <h5 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#F7E9A0',
                        marginBottom: '8px'
                      }}>
                        Clinical Notes
                      </h5>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#16325B',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        color: '#e5e7eb',
                        lineHeight: '1.6'
                      }}>
                        {(() => {
                          const text: string = session.clinicalNotes;
                          // Try to split by section headers (## Subjective, SUBJECTIVE:, --- SUBJECTIVE, etc.)
                          const headerKeywords = ['SUBJECTIVE', 'OBJECTIVE', 'ASSESSMENT', 'PLAN', 'DATA', 'BEHAVIOR', 'INTERVENTION', 'RESPONSE', 'PROGRESS', 'GOALS', 'BARRIERS', 'NEXT STEPS', 'PRESENTING PROBLEM', 'HISTORY', 'MENTAL STATUS', 'DIAGNOSIS', 'RECOMMENDATIONS', 'WARNINGS'];
                          const sectionPattern = new RegExp(`(?:^|\\n)\\s*(?:---\\s*)?(?:##?\\s*)?(?:\\*\\*)?(${headerKeywords.join('|')})(?:\\*\\*)?s?\\s*:?\\s*(?:---)?`, 'gi');
                          const parts: { header: string; body: string }[] = [];
                          let match;
                          const matchList: { header: string; index: number; length: number }[] = [];
                          while ((match = sectionPattern.exec(text)) !== null) {
                            matchList.push({ header: match[1], index: match.index, length: match[0].length });
                          }
                          if (matchList.length >= 2) {
                            for (let i = 0; i < matchList.length; i++) {
                              const startPos = matchList[i].index + matchList[i].length;
                              const endPos = i + 1 < matchList.length ? matchList[i + 1].index : text.length;
                              parts.push({ header: matchList[i].header, body: text.slice(startPos, endPos).trim() });
                            }
                            return parts.map((part, i) => (
                              <div key={i} style={{ marginBottom: '14px' }}>
                                <h6 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>
                                  {part.header}
                                </h6>
                                <p style={{ color: '#e5e7eb', margin: 0, whiteSpace: 'pre-wrap' }}>{part.body}</p>
                              </div>
                            ));
                          }
                          // Fallback: render as-is with line breaks
                          return <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{text}</p>;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Clinical Notes Editor Modal */}
      {showNotesEditor && selectedSessionForNotes && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          overflowY: 'auto'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '1400px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <ClinicalNotesEditor
              sessionId={selectedSessionForNotes.id}
              patientId={client.id}
              providerId={user?.uid || ''}
              transcript={selectedSessionForNotes.filteredTranscript || selectedSessionForNotes.originalTranscript || ''}
              sessionType={selectedSessionForNotes.sessionType}
              sessionDuration={selectedSessionForNotes.duration}
              patientHistory={{
                diagnoses: (client as any).diagnoses?.map((d: any) => d.name) || [],
                medications: (client as any).prescriptions?.map((p: any) => p.medicationName) || [],
                previousNotes: []
              }}
              onNoteSaved={handleNoteSaved}
              onClose={handleCloseNotesEditor}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsTabImplementation;
